import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './ConfigurationEditor.html?raw';

import _StatefulSection from '../../../moreCore/UI/_StatefulSection/_StatefulSection';
import _uiFrameMixin from '../../../moreCore/UI/Container/uiFrame';
import { Configuration } from "../ScreenplayTypes";
import { TabContainer } from "../../../moreCore/_loadingHelpers/Tabbing";
import lang from 'dojo/_base/lang';
import GenericDialog from '../../../moreCore/UI/GenericDialog/GenericDialog';

class ConfigurationEditor extends compose(_StatefulSection, _uiFrameMixin) {
    templateString: any = template;

    setup(tabContainer: MdsLax<InstanceType<typeof TabContainer>>, entity?: Configuration): void {
        let heading;

        if (entity) {
            this.internalName = "configurationEditor_" + entity.ID;
            entity.is_new = false;
            heading = `${this.translate("Screenplay_Heading_Configuration")}: ${entity.description}`;
            this.setStartupState("ready");
            this._loadEntity(entity.ID);
        } else {
            this.internalName = "configurationEditor_New";
            let newEntity: Configuration = {
                ID: "",
                $Displayname: "",
                Classname: "",
                ClassPrintname: "",
                description: "",
                fallback_image: "",
                image_folder: "",
                is_new: true
            }
            entity = newEntity;
            this.setData(entity);
            heading = this.translate("Screenplay_Heading_NewConfiguration")
        }

        if (tabContainer) {
            this.show("tab", heading, tabContainer.name);
        } else {
            this.show("dlg", heading, tabContainer.name);
        }

        this.createDefaultButton("save", lang.hitch(this, this._onClickSave), false, "primary");
    }

    private _loadEntity(entityId: string): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Configuration_LoadEntity",
            { ID: entityId },
            lang.hitch(this, this._onLoadEntityResponse),
            lang.hitch(this, this._onLoadEntityError));
    }

    private _onLoadEntityResponse(response: Configuration): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        response.is_new = false;
        this.setData(response);
    }

    private _onLoadEntityError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _onClickSave(): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);

        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        let isNew = this.getAttributeValue("is_new");
        let changedData = this.getChangedData();
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Configuration_SaveEntity",
            { ID: entityId, Classname: classname, is_new: isNew, changedData: changedData },
            lang.hitch(this, this._onSaveResponse),
            lang.hitch(this, this._onSaveError));
    }

    private _onSaveResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.emit("saved");
        this._loadEntity(response.entityId);
    }

    private _onSaveError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    } 

    private _onClickRollback(e): void {
        this.rollback();
    }

    private _onClickReload(e): void {
        let entity = this.getAttributeValue("___root");
        this._loadEntity(entity.ID);
    }

    private _onClickDelete(e): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Configuration_DeleteEntity",
            { ID: entityId, Classname: classname },
            lang.hitch(this, this._onDeleteResponse),
            lang.hitch(this, this._onDeleteError));
    }

    private _onDeleteResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.close();
    }

    private _onDeleteError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _deactivateDataButtons(deactivate: boolean, deactivateAll?: boolean): void {
        this.requestContextButtonDisable("save", deactivate);
        this.requestContextButtonDisable("rollback", deactivate);
        if (deactivateAll) {
            this.requestContextButtonDisable("delete", deactivate);
            this.requestContextButtonDisable("reload", deactivate);
        }
    }

    _onCloseRequest() {
        let hasChanged = this.isDirty() === true;

        if (hasChanged) {
            let dlgConfirm = new GenericDialog();
            dlgConfirm.show(this.translations["General_AskSavePendingChanges"],
                this.translations["General_PendingChanges"],
                "",
                "",
                "yesnocancel",
                lang.hitch(this, this._confirmCallback));

            return false;

        } else {
            return true;
        }
    }

    private _confirmCallback(result) {
        if (result == -1) { // nein
            this.close();
        } else if (result == 0) { // cancel

        }
        else {
            this._onClickSave();
            this.on("saved", lang.hitch(this, this.close));
        }
    }

    protected afterSetData(): void {
        super.afterSetData.apply(this, arguments as any);

        let isNew = this.getAttributeValue("is_new");

        if (!isNew) {
            this.createDefaultButton("delete", lang.hitch(this, this._onClickDelete), true, "secondary");
            this.createDefaultButton("rollback", lang.hitch(this, this._onClickRollback), false, "secondary");
            this.createDefaultButton("reload", lang.hitch(this, this._onClickReload), true, "primary");
            this.changeInternalName("configurationEditor_" + this.getAttributeValue("ID"));
            this.setTitles(`${this.translate("Screenplay_Heading_ExternalUrl")}: ${this.getAttributeValue("description")}`);
        } else {

        }
        this._deactivateDataButtons(true);
    }

    protected _onChanged(attribute: string, value: any) {
        switch (attribute) {

        }
    }

    protected onDirtyChange(newState: boolean) {
        this._deactivateDataButtons(!newState);
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(ConfigurationEditor);